export type AnalyticsEventValue = string | number | boolean;

const MAX_FIELD_KEY_LENGTH = 40;
const MAX_FIELD_VALUE_LENGTH = 120;
const MAX_FIELD_COUNT = 20;
const MAX_SNAPSHOT_LENGTH = 900;

const SENSITIVE_FIELD_HINTS = [
	"password",
	"passcode",
	"otp",
	"token",
	"secret",
	"captcha",
	"cvv",
	"card",
	"pan",
	"aadhaar",
	"aadhar",
	"ssn",
	"email",
	"mail",
	"phone",
	"mobile",
	"whatsapp",
] as const;

const PERSON_NAME_FIELD_HINTS = [
	"full_name",
	"first_name",
	"middle_name",
	"last_name",
] as const;

const SAFE_NAME_CONTEXT_HINTS = [
	"country",
	"college",
	"course",
	"program",
	"university",
	"event",
	"city",
	"state",
	"company",
	"institution",
] as const;

const FREE_TEXT_FIELD_HINTS = [
	"message",
	"comment",
	"feedback",
	"description",
	"query",
	"note",
	"reason",
] as const;

const normalizeWhitespace = (value: string): string =>
	value.replace(/\s+/g, " ").trim();

const normalizeFieldKey = (value: string): string =>
	value
		.toLowerCase()
		.trim()
		.replace(/([a-z0-9])([A-Z])/g, "$1_$2")
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, MAX_FIELD_KEY_LENGTH);

const includesAny = (value: string, hints: readonly string[]) =>
	hints.some((hint) => value.includes(hint));

const maskEmail = (value: string): string => {
	const normalized = normalizeWhitespace(value).toLowerCase();
	const [localPart = "", domain = ""] = normalized.split("@");
	if (!localPart || !domain) {
		return "[redacted_email]";
	}

	const visibleLocal = localPart.slice(0, Math.min(2, localPart.length));
	return `${visibleLocal}***@${domain}`;
};

const maskPhone = (value: string): string => {
	const digits = value.replace(/\D/g, "");
	if (!digits) {
		return "[redacted_phone]";
	}

	return `***${digits.slice(-4)}`;
};

const maskName = (value: string): string => {
	const parts = normalizeWhitespace(value)
		.split(" ")
		.filter(Boolean);
	if (parts.length === 0) {
		return "[redacted_name]";
	}

	return parts
		.map((part) => `${part[0]?.toUpperCase() || ""}***`)
		.join(" ");
};

const looksLikeEmail = (value: string): boolean =>
	/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const looksLikePhone = (value: string): boolean =>
	value.replace(/\D/g, "").length >= 7;

const isPersonNameKey = (key: string): boolean => {
	if (includesAny(key, PERSON_NAME_FIELD_HINTS)) {
		return true;
	}

	return key.includes("name") && !includesAny(key, SAFE_NAME_CONTEXT_HINTS);
};

const sanitizeStringValue = (
	rawKey: string,
	rawValue: string,
): AnalyticsEventValue | undefined => {
	const key = normalizeFieldKey(rawKey);
	const value = normalizeWhitespace(rawValue);

	if (!key || !value) {
		return undefined;
	}

	if (includesAny(key, FREE_TEXT_FIELD_HINTS)) {
		const wordCount = value.split(" ").filter(Boolean).length;
		return `[text:${wordCount}_words]`;
	}

	if (includesAny(key, SENSITIVE_FIELD_HINTS)) {
		if (key.includes("email") || key.includes("mail") || looksLikeEmail(value)) {
			return maskEmail(value);
		}

		if (
			key.includes("phone") ||
			key.includes("mobile") ||
			key.includes("whatsapp")
		) {
			return maskPhone(value);
		}

		return "[redacted]";
	}

	if (looksLikeEmail(value)) {
		return maskEmail(value);
	}

	if (looksLikePhone(value)) {
		return maskPhone(value);
	}

	if (isPersonNameKey(key)) {
		return maskName(value);
	}

	return value.slice(0, MAX_FIELD_VALUE_LENGTH);
};

const sanitizeValue = (
	rawKey: string,
	value: unknown,
): AnalyticsEventValue | undefined => {
	if (value === null || value === undefined) {
		return undefined;
	}

	if (typeof value === "boolean" || typeof value === "number") {
		return value;
	}

	if (typeof value === "string") {
		return sanitizeStringValue(rawKey, value);
	}

	if (Array.isArray(value)) {
		const items = value
			.map((item) => sanitizeValue(rawKey, item))
			.filter(
				(item): item is AnalyticsEventValue =>
					item !== undefined && item !== null && item !== "",
			)
			.map(String);

		if (items.length === 0) {
			return undefined;
		}

		return items.join("|").slice(0, MAX_FIELD_VALUE_LENGTH);
	}

	return sanitizeStringValue(rawKey, String(value));
};

const getFieldIdentifier = (field: HTMLElement): string | null => {
	return (
		field.getAttribute("name") ||
		field.getAttribute("id") ||
		field.getAttribute("aria-label") ||
		field.getAttribute("placeholder")
	);
};

const appendFieldValue = (
	accumulator: Record<string, unknown>,
	key: string,
	value: unknown,
) => {
	if (value === undefined || value === null || value === "") {
		return;
	}

	const existingValue = accumulator[key];
	if (existingValue === undefined) {
		accumulator[key] = value;
		return;
	}

	if (Array.isArray(existingValue)) {
		existingValue.push(value);
		return;
	}

	accumulator[key] = [existingValue, value];
};

export const extractAnalyticsFormFields = (
	root: ParentNode,
): Record<string, unknown> => {
	const fields = root.querySelectorAll("input, textarea, select");
	const values: Record<string, unknown> = {};

	fields.forEach((field) => {
		if (!(field instanceof HTMLElement)) {
			return;
		}

		const key = getFieldIdentifier(field);
		if (!key) {
			return;
		}

		if ("disabled" in field && field.disabled) {
			return;
		}

		if (field instanceof HTMLInputElement) {
			const inputType = field.type.toLowerCase();
			if (
				["hidden", "password", "file", "submit", "button", "reset", "image"].includes(
					inputType,
				)
			) {
				return;
			}

			if (inputType === "checkbox") {
				if (field.checked) {
					appendFieldValue(
						values,
						key,
						field.value && field.value !== "on" ? field.value : true,
					);
				}
				return;
			}

			if (inputType === "radio") {
				if (field.checked) {
					appendFieldValue(values, key, field.value || true);
				}
				return;
			}

			appendFieldValue(values, key, field.value);
			return;
		}

		if (field instanceof HTMLTextAreaElement) {
			appendFieldValue(values, key, field.value);
			return;
		}

		if (field instanceof HTMLSelectElement) {
			if (field.multiple) {
				const selectedValues = Array.from(field.selectedOptions)
					.map((option) => option.value)
					.filter(Boolean);
				appendFieldValue(values, key, selectedValues);
				return;
			}

			appendFieldValue(values, key, field.value);
		}
	});

	return values;
};

export const buildAnalyticsFormDataProps = (
	formData: Record<string, unknown>,
): Record<string, AnalyticsEventValue> => {
	const sanitizedEntries = Object.entries(formData)
		.map(([rawKey, rawValue]) => {
			const key = normalizeFieldKey(rawKey);
			const value = sanitizeValue(rawKey, rawValue);
			return key && value !== undefined ? ([key, value] as const) : null;
		})
		.filter(
			(entry): entry is readonly [string, AnalyticsEventValue] => entry !== null,
		)
		.slice(0, MAX_FIELD_COUNT);

	if (sanitizedEntries.length === 0) {
		return {};
	}

	const fittedEntries: Array<readonly [string, AnalyticsEventValue]> = [];

	for (const entry of sanitizedEntries) {
		const nextEntries = [...fittedEntries, entry];
		const nextSnapshot = JSON.stringify(Object.fromEntries(nextEntries));

		if (nextSnapshot.length > MAX_SNAPSHOT_LENGTH) {
			break;
		}

		fittedEntries.push(entry);
	}

	const snapshotObject = Object.fromEntries(fittedEntries);
	return {
		filled_fields_count: fittedEntries.length,
		filled_fields_truncated: fittedEntries.length < sanitizedEntries.length,
		filled_field_names: fittedEntries.map(([key]) => key).join(","),
		form_data: JSON.stringify(snapshotObject),
	};
};
